import type {
  BasketSyncInput,
  BasketSyncStep,
  RefreshMarkInput,
} from "./types";

export const TPA_BALANCE_RESOURCE = "/resources/tpa-balance";

/** The resource path; `?refresh=true` asks the loader for a MuleSoft re-pull. */
export const tpaBalanceResourcePath = (forceRefresh: boolean): string =>
  forceRefresh ? `${TPA_BALANCE_RESOURCE}?refresh=true` : TPA_BALANCE_RESOURCE;

/**
 * Rule: the first fetch for a logged-in shopper in this tab forces the MuleSoft
 * refresh (COM-674 — consent may have been revoked at the TPA since the stored
 * balance was written). Guests never force; a shopper already attempted in
 * this tab never forces again.
 */
export const needsForcedRefresh = (
  shopperId: string | null,
  attemptedFor: string | null,
): boolean => shopperId !== null && attemptedFor !== shopperId;

/**
 * Rule: a forced fetch counts as THE attempt for this login once it settles,
 * whatever the outcome — ok, error payload, network failure, or the server's
 * pre-SFCC-12393 400 fallback to a plain read. Retrying a failing forced call
 * would re-run the 25s MuleSoft call on every cart change, so it is capped at
 * one per login per tab.
 */
export const shouldMarkRefreshAttempted = ({
  forceRefresh,
  shopperId,
  isLoading,
}: RefreshMarkInput): boolean =>
  forceRefresh && shopperId !== null && !isLoading;

/**
 * Rule: reload the balance when the basket total changes under a live tracker,
 * so its session usage (used / left-to-spend / %) moves with the cart. The
 * first settled total is only the baseline — the load that produced the
 * tracker already read the basket server-side, so a cold load never
 * double-fetches. No tracker, no reload: each one is a custom-API call, and
 * the baseline is dropped so a tracker that appears later starts fresh.
 */
export const basketSyncStep = ({
  baseline,
  total,
  tracking,
}: BasketSyncInput): BasketSyncStep => {
  if (!tracking) return { baseline: undefined, reload: false };
  if (total === undefined) return { baseline, reload: false };
  if (baseline === undefined) return { baseline: total, reload: false };
  return { baseline: total, reload: total !== baseline };
};
